import React, { useEffect, useRef, useState } from "react";
import contacts from "../../constants/contact-us.json";
import { WEBSITE_SEO, DEFAULT_OG_IMAGE } from "../../constants/websiteSeo";
import { WEBSITE_PAGES } from "../../constants/websiteRoutes";
import { readHeadFromDocument, syncDocumentHead } from "../../utils/syncDocumentHead";
import styles from "./FramerWebsite.module.scss";
import requester from "../../utils/requester";
import { toast } from "react-toastify";
import history from "../../utils/history";

const SOCIAL_LINKS_BY_LABEL = {
  instagram: contacts.instagram,
  facebook: contacts.facebook,
  youtube: contacts.youtube,
  tiktok: contacts.tiktok,
  twitter: contacts.tiktok,
  whatsapp: contacts.whatsapp,
  pinterest: contacts.pinterest,
};

const SOCIAL_CONTAINERS =
  ".framer-2zzrkt, .framer-J33ir, .framer-ehybib, .framer-er5rzn";

const SOCIAL_LINKS_BY_DOMAIN = [
  ["instagram.com", contacts.instagram],
  ["facebook.com", contacts.facebook],
  ["tiktok.com", contacts.tiktok],
  ["twitter.com", contacts.tiktok],
  ["x.com", contacts.tiktok],
  ["youtube.com", contacts.youtube],
  ["pinterest", contacts.pinterest],
  ["wa.me", contacts.whatsapp],
  ["whatsapp.com", contacts.whatsapp],
];

const BADGE_STYLE =
  "#__framer-badge-container, #__framer-badge-container *, .__framer-badge, [class*='framer-badge'], [id*='framer-badge'] { display: none !important; visibility: hidden !important; pointer-events: none !important; }";

const GET_YOUR_MOMENTS_FORM_SELECTOR = "form.framer-1k8piij";
const CONTACT_FORM_SELECTOR = "form.framer-9nuax7";

const CONTACT_HONEYPOT_FIELDS = [
  "website",
  "company",
  "subject",
  "title",
  "description",
  "feedback",
  "notes",
  "details",
  "remarks",
  "comments",
];

const ALL_ENDPOINTS = [
  "/albums/getAlbumImages",
  "/aquarium/get-ticket-images",
  "/media/get-ticket-images",
  "/dolphin-house/get-ticket-images",
];

function getSocialUrl(label) {
  const key = label.trim().toLowerCase();
  return SOCIAL_LINKS_BY_LABEL[key];
}

const SOCIAL_LINK_SELECTOR = [
  "a.framer-6z9iF",
  "a.framer-kHJA1",
  `${SOCIAL_CONTAINERS} a[href]`,
].join(", ");

function getLinkLabel(link) {
  const labelNode = link.querySelector(
    "p.framer-text, [data-framer-component-type='RichTextContainer'] p"
  );
  return (labelNode?.textContent || link.textContent || "").trim();
}

function isSidebarSocialLink(link) {
  if (getSocialUrl(getLinkLabel(link))) return true;

  return (
    link.classList.contains("framer-6z9iF") ||
    link.classList.contains("framer-kHJA1") ||
    link.closest(SOCIAL_CONTAINERS) !== null
  );
}

function hostnameOf(href) {
  try {
    return new URL((href || "").replace(/\s+/g, ""), "https://www.fimtosoft.net")
      .hostname.replace(/^www\./, "")
      .toLowerCase();
  } catch {
    return "";
  }
}

function hostnameIs(href, domain) {
  const host = hostnameOf(href);
  const d = String(domain || "").replace(/^www\./, "").toLowerCase();
  if (!host || !d) return false;
  if (d === "pinterest") {
    return host === "pinterest.com" || host === "pin.it" || host.endsWith(".pinterest.com");
  }
  return host === d || host.endsWith(`.${d}`);
}

function isAlbumChoiceLink(link) {
  if (link.hasAttribute("data-fimto-album")) return true;
  return (
    link.classList.contains("button") && /^(photo|video)$/i.test(getLinkLabel(link))
  );
}

function isPinterestHref(href) {
  const host = hostnameOf(href);
  return host === "pin.it" || host === "pinterest.com" || host.endsWith(".pinterest.com");
}

function resolveSocialUrl(link) {
  if (isAlbumChoiceLink(link)) return null;

  const label = getLinkLabel(link);
  const byLabel = label ? getSocialUrl(label) : null;
  if (byLabel && !isPinterestHref(byLabel)) return byLabel;

  if (!isSidebarSocialLink(link)) return null;

  const href = (link.getAttribute("href") || "").replace(/\s+/g, "");
  for (const [domain, url] of SOCIAL_LINKS_BY_DOMAIN) {
    if (!url || isPinterestHref(url)) continue;
    if (hostnameIs(href, domain)) return url;
  }
  return null;
}

function syncAnchor(link) {
  const socialUrl = resolveSocialUrl(link);
  if (!socialUrl) return false;

  let changed = false;
  const currentHref = link.getAttribute("href") || "";

  if (currentHref !== socialUrl) {
    link.setAttribute("href", socialUrl);
    changed = true;
  }
  if (link.target !== "_blank") link.target = "_blank";
  if (link.rel !== "noopener noreferrer") link.rel = "noopener noreferrer";
  return changed;
}

// Keep burger-menu / sidebar social buttons pointed at Fimtosoft profiles.
// Framer may re-render these links from its own component data after hydration.
function updateSocialLinks(doc) {
  if (!doc) return false;

  let changed = false;
  doc.querySelectorAll("a[href]").forEach((link) => {
    if (isAlbumChoiceLink(link)) return;
    const href = link.getAttribute("href") || "";
    if (
      (isPinterestHref(href) || /^pinterest$/i.test(getLinkLabel(link))) &&
      (isSidebarSocialLink(link) || /^pinterest$/i.test(getLinkLabel(link)))
    ) {
      concealFramerNode(link);
      changed = true;
      return;
    }
    if (syncAnchor(link)) changed = true;
  });

  const mailto = `mailto:${contacts.email}`;
  doc.querySelectorAll("a[href]").forEach((link) => {
    const href = link.getAttribute("href") || "";
    const compact = href.replace(/\s+/g, "").toLowerCase();
    const text = getLinkLabel(link);
    const isMailto = compact.startsWith("mailto:");
    if (
      !isMailto &&
      !text.includes("@")
    ) {
      return;
    }
    if (
      !isMailto &&
      !/@fimtosoft\.net/i.test(text) &&
      !/@email\.com/i.test(`${href} ${text}`)
    ) {
      return;
    }
    if (link.getAttribute("href") !== mailto) {
      link.setAttribute("href", mailto);
      changed = true;
    }
    if (text.includes("@") && text !== contacts.email) {
      const labelNode = link.querySelector("p.framer-text") || link;
      labelNode.textContent = contacts.email;
      changed = true;
    }
  });

  return changed;
}

function socialLinksNeedSync(doc) {
  return Array.from(doc.querySelectorAll("a[href]")).some((link) => {
    const href = link.getAttribute("href") || "";
    if (isPinterestHref(href) || /^pinterest$/i.test(getLinkLabel(link))) {
      return !link.hasAttribute("hidden");
    }
    const expected = resolveSocialUrl(link);
    return Boolean(expected) && href !== expected;
  });
}

function toTelHref(phone) {
  const digits = String(phone || "").replace(/\D/g, "");
  return digits ? `tel:+${digits}` : "";
}

function matchingOfficialPhone(text) {
  const digits = String(text || "").replace(/\D/g, "");
  if (digits.length < 10) return null;
  for (const phone of [contacts.phone1, contacts.phone2].filter(Boolean)) {
    const official = String(phone).replace(/\D/g, "");
    if (digits === official || digits.slice(-official.length) === official) return phone;
  }
  return null;
}

function syncTelLinks(doc) {
  if (!doc?.body || !contacts.phone1) return;

  const walker = doc.createTreeWalker(doc.body, doc.defaultView.NodeFilter.SHOW_TEXT, null);
  const nodes = [];
  while (walker.nextNode()) nodes.push(walker.currentNode);
  nodes.forEach((node) => {
    const parent = node.parentElement;
    if (!parent?.closest || parent.closest("a, script, style, textarea, input, form")) return;
    const phone = matchingOfficialPhone(node.nodeValue);
    if (!phone) return;
    const link = doc.createElement("a");
    link.href = toTelHref(phone);
    link.textContent = node.nodeValue;
    link.style.color = "inherit";
    link.style.textDecoration = "underline";
    parent.replaceChild(link, node);
  });

  const href = toTelHref(contacts.phone1);
  doc.querySelectorAll("footer.framer-J33ir").forEach((footer) => {
    const existing = footer.querySelector('a[href^="tel:"]');
    if (existing) {
      if (existing.getAttribute("href") !== href) existing.setAttribute("href", href);
      return;
    }
    const host =
      footer.querySelector(".framer-my2lcm") ||
      footer.querySelector(".framer-er5rzn") ||
      footer;
    const link = doc.createElement("a");
    link.setAttribute("data-fimto-tel", "footer");
    link.href = href;
    link.textContent = contacts.phone1;
    link.style.cssText =
      "display:block;text-align:center;color:inherit;text-decoration:underline;margin:.45rem 0 0";
    host.appendChild(link);
  });
}

function concealFramerNode(el) {
  if (!el) return;
  el.setAttribute("hidden", "");
  el.setAttribute("aria-hidden", "true");
  el.style.setProperty("display", "none", "important");
  el.style.setProperty("visibility", "hidden", "important");
  el.style.setProperty("pointer-events", "none", "important");
}

function isFramerBadgeLink(link) {
  const href = (link.getAttribute("href") || "").replace(/\s+/g, "");
  if (!href || /framerusercontent\.com|events\.framer\.com/i.test(href)) return false;
  try {
    const url = new URL(href, "https://www.fimtosoft.net");
    const host = url.hostname.replace(/^www\./, "");
    if (host !== "framer.com" || url.pathname.startsWith("/edit")) return false;
  } catch {
    return false;
  }
  const label = `${link.textContent || ""} ${link.getAttribute("aria-label") || ""}`.toLowerCase();
  if (/create a free website|made (with|in) framer/.test(label)) return true;
  return /^(https?:)?\/\/(www\.)?framer\.com\/?(\?|#|$)/i.test(href);
}

function hideFramerBadge(doc) {
  if (!doc) return;

  let style = doc.getElementById("hide-framer-badge");
  if (!style) {
    style = doc.createElement("style");
    style.id = "hide-framer-badge";
    doc.head.appendChild(style);
  }
  style.textContent = BADGE_STYLE;

  // Keep the hydrate target in the DOM (Framer throws if it is missing) but
  // conceal it and any badge link Framer portals outside the container.
  concealFramerNode(doc.getElementById("__framer-badge-container"));
  doc.querySelectorAll(".__framer-badge, [class*='framer-badge']").forEach(concealFramerNode);
  doc.querySelectorAll("a[href]").forEach((link) => {
    if (isFramerBadgeLink(link)) concealFramerNode(link);
  });
}

function trimGetYourMomentsForm(doc) {
  if (!doc) return;

  if (!doc.getElementById("trim-form-style")) {
    const style = doc.createElement("style");
    style.id = "trim-form-style";
    style.textContent = `
      form.framer-1k8piij input:not([name="Code"]):not([aria-hidden="true"]),
      form.framer-1k8piij textarea,
      form.framer-1k8piij label:not(.framer-bulutq) {
        display: none !important;
      }
    `;
    doc.head.appendChild(style);
  }

  disableGetYourMomentsFormValidation(doc);
}

function disableGetYourMomentsFormValidation(doc) {
  const form = doc.querySelector(GET_YOUR_MOMENTS_FORM_SELECTOR);
  if (!form) return;

  form.setAttribute("novalidate", "");
  form.querySelectorAll('input:not([name="Code"])').forEach((input) => {
    input.removeAttribute("required");
  });
}

function getCodeFromGetYourMomentsForm(doc, initialCode) {
  const input = doc.querySelector(
    `${GET_YOUR_MOMENTS_FORM_SELECTOR} input[name="Code"]`
  );
  return (input?.value || initialCode || "").trim();
}

function triggerGetYourMomentsSubmit(doc, initialCode, onFormSubmitRef) {
  const code = getCodeFromGetYourMomentsForm(doc, initialCode);
  if (code) {
    onFormSubmitRef.current?.(code);
  }
}

const FRAMER_PAGE_ROUTES = {
  "./": "/",
  ".": "/",
  "./index.html": "/",
  "./clients": "/clients",
  "./clients/": "/clients",
  "./services": "/services",
  "./services/": "/services",
  "./contact": "/contact",
  "./contact/": "/contact",
  "./get-your-moments": "/get-your-moments",
  "./get-your-moments/": "/get-your-moments",
};

function interceptFramerPageLinks(doc) {
  if (!doc || doc.__framerPageLinksIntercepted) return;
  doc.__framerPageLinksIntercepted = true;

  doc.addEventListener(
    "click",
    (e) => {
      const anchor = e.target.closest("a[href]");
      if (!anchor) return;

      const href = anchor.getAttribute("href") || "";
      const route = FRAMER_PAGE_ROUTES[href];
      if (!route) return;

      e.preventDefault();
      e.stopImmediatePropagation();
      history.push(route);
    },
    true
  );
}

// Intercept any link inside the iframe that points to the Framer
// get-your-moments page and redirect the top-level window to the React route.
function interceptGetYourMomentsLinks(doc) {
  if (!doc || doc.__gymLinksIntercepted) return;
  doc.__gymLinksIntercepted = true;

  doc.addEventListener("click", (e) => {
    const anchor = e.target.closest("a[href]");
    if (!anchor) return;
    const href = anchor.getAttribute("href");
    if (href && href.includes("get-your-moments")) {
      e.preventDefault();
      e.stopImmediatePropagation();
      history.push("/get-your-moments");
    }
  }, true);
}

// Ensure absolute external links (e.g. social media icons in the mobile nav)
// always open in a new tab. Framer's dynamically-rendered nav links may lack
// target="_blank", which causes them to navigate the iframe instead.
function interceptExternalLinks(doc) {
  if (!doc || doc.__externalLinksIntercepted) return;
  doc.__externalLinksIntercepted = true;

  doc.addEventListener("click", (e) => {
    const anchor = e.target.closest("a[href]");
    if (!anchor) return;

    const socialUrl = resolveSocialUrl(anchor);
    if (socialUrl) {
      e.preventDefault();
      e.stopImmediatePropagation();
      window.open(socialUrl, "_blank", "noopener,noreferrer");
      return;
    }

    const href = anchor.getAttribute("href");
    if (!href || (!href.startsWith("http://") && !href.startsWith("https://"))) return;
    e.preventDefault();
    e.stopImmediatePropagation();
    window.open(href, "_blank", "noopener,noreferrer");
  }, true);
}

function applyPageCustomizations(doc, { syncSocialLinks = true } = {}) {
  hideFramerBadge(doc);
  if (syncSocialLinks && socialLinksNeedSync(doc)) {
    updateSocialLinks(doc);
  }
  syncTelLinks(doc);
  trimGetYourMomentsForm(doc);
  interceptExternalLinks(doc);
}

function runIframeCustomizations(
  doc,
  observer,
  initialCode,
  onFormSubmitRef,
  onContactSubmitRef,
  options
) {
  observer?.disconnect();
  try {
    applyPageCustomizations(doc, options);
    enhanceGetYourMomentsForm(doc, initialCode, onFormSubmitRef);
    enhanceContactForm(doc, onContactSubmitRef);
  } finally {
    if (doc.documentElement) {
      observer?.observe(doc.documentElement, { childList: true, subtree: true });
    }
  }
}

function enhanceGetYourMomentsForm(doc, initialCode, onFormSubmitRef) {
  if (!doc) return;

  doc.__gymInitialCode = initialCode;
  disableGetYourMomentsFormValidation(doc);

  // Attach interceptors once per document. Framer hydration may replace the
  // form node and handle submit via click instead of a native submit event.
  if (!doc.__gymSubmitEnhanced) {
    doc.__gymSubmitEnhanced = true;

    doc.addEventListener(
      "submit",
      (e) => {
        if (!e.target.matches(GET_YOUR_MOMENTS_FORM_SELECTOR)) return;
        e.preventDefault();
        e.stopImmediatePropagation();
        triggerGetYourMomentsSubmit(doc, doc.__gymInitialCode, onFormSubmitRef);
      },
      true
    );

    doc.addEventListener(
      "click",
      (e) => {
        const button = e.target.closest(
          `${GET_YOUR_MOMENTS_FORM_SELECTOR} button[type="submit"]`
        );
        if (!button) return;
        e.preventDefault();
        e.stopImmediatePropagation();
        triggerGetYourMomentsSubmit(doc, doc.__gymInitialCode, onFormSubmitRef);
      },
      true
    );
  }

  // Pre-fill the Code input whenever it appears in the DOM.
  if (initialCode) {
    const form = doc.querySelector(GET_YOUR_MOMENTS_FORM_SELECTOR);
    const input = form?.querySelector('input[name="Code"]');
    if (input && !input.dataset.prefilled) {
      input.dataset.prefilled = "true";
      const win = doc.defaultView;
      const nativeSetter = Object.getOwnPropertyDescriptor(
        win.HTMLInputElement.prototype,
        "value"
      ).set;
      nativeSetter.call(input, initialCode);
      input.dispatchEvent(new win.Event("input", { bubbles: true }));
      input.dispatchEvent(new win.Event("change", { bubbles: true }));
    }
  }
}

function showContactStatus(form, message, kind) {
  let status = form.querySelector("[data-fimto-form-status]");
  if (!status) {
    status = form.ownerDocument.createElement("p");
    status.setAttribute("data-fimto-form-status", "");
    status.setAttribute("role", "status");
    status.style.cssText =
      "margin:1rem 0 0;text-align:center;font-family:sans-serif;font-size:.95rem;line-height:1.45";
    form.appendChild(status);
  }
  status.textContent = message;
  status.style.color = kind === "error" ? "#ffb4b4" : "#b7f5c6";
}

function setContactFormBusy(form, busy) {
  const button = form.querySelector('button[type="submit"]');
  if (!button) return;
  button.disabled = !!busy;
  button.setAttribute("aria-busy", busy ? "true" : "false");
}

function clearContactFields(form) {
  const win = form.ownerDocument.defaultView;
  form.reset();
  ["Name", "Email", "Phone Number"].forEach((name) => {
    const field = form.querySelector(`input[name="${name}"]`);
    if (!field) return;
    const setter = Object.getOwnPropertyDescriptor(win.HTMLInputElement.prototype, "value")?.set;
    setter ? setter.call(field, "") : (field.value = "");
    field.dispatchEvent(new win.Event("input", { bubbles: true }));
  });
  const message = form.querySelector('textarea[name="Message"]');
  if (message) {
    const setter = Object.getOwnPropertyDescriptor(
      win.HTMLTextAreaElement.prototype,
      "value"
    )?.set;
    setter ? setter.call(message, "") : (message.value = "");
    message.dispatchEvent(new win.Event("input", { bubbles: true }));
  }
}

function contactFailureMessage() {
  const phone = contacts.phone1 || "";
  const base =
    "Could not send your message. Please try again, or WhatsApp / call us.";
  return phone && !base.includes(phone) ? `${base.replace(/\.\s*$/, "")} ${phone}.` : base;
}

function collectContactFormPayload(form) {
  const payload = {
    name: form.querySelector('input[name="Name"]')?.value?.trim() || "",
    email: form.querySelector('input[name="Email"]')?.value?.trim() || "",
    phone: form.querySelector('input[name="Phone Number"]')?.value?.trim() || "",
    message: form.querySelector('textarea[name="Message"]')?.value?.trim() || "",
    honeypotMessage: form.querySelector('input[name="message"]')?.value?.trim() || "",
  };

  CONTACT_HONEYPOT_FIELDS.forEach((field) => {
    payload[field] = form.querySelector(`input[name="${field}"]`)?.value?.trim() || "";
  });

  return payload;
}

function enhanceContactForm(doc, onContactSubmitRef) {
  if (!doc) return;

  doc.querySelectorAll('textarea[name="Message"]').forEach((field) => {
    if (/Messsage/i.test(field.getAttribute("placeholder") || "")) {
      field.setAttribute("placeholder", "Enter your Message");
    }
  });
  doc.querySelectorAll("p, span, h1, h2, h3").forEach((node) => {
    if (node.children.length) return;
    if ((node.textContent || "").trim() !== "Send Me a Message") return;
    node.textContent = "Send Us a Message";
  });

  if (!doc.__contactSubmitEnhanced) {
    doc.__contactSubmitEnhanced = true;
    doc.addEventListener(
      "submit",
      (e) => {
        if (!e.target.matches(CONTACT_FORM_SELECTOR)) return;
        e.preventDefault();
        e.stopImmediatePropagation();
        onContactSubmitRef.current?.(collectContactFormPayload(e.target), e.target);
      },
      true
    );
  }
}

function applyWebsiteHead(page, iframeDoc) {
  const fallback = WEBSITE_SEO[page] || WEBSITE_SEO["/"];
  const fromIframe = readHeadFromDocument(iframeDoc);

  syncDocumentHead({
    // The configured copy wins: several Framer pages carry a stale default title.
    title: fallback.title || fromIframe?.title,
    description: fallback.description || fromIframe?.description,
    canonical: fallback.canonical,
    ogImage: fromIframe?.ogImage || DEFAULT_OG_IMAGE,
  });
}

export default function FramerWebsite({ page = "/", initialCode, onFormSubmit }) {
  const iframeRef = useRef(null);
  const src = WEBSITE_PAGES[page] || WEBSITE_PAGES["/"];
  const publicUrl = process.env.PUBLIC_URL || "";

  const [album, setAlbum] = useState(null);
  const [isLoading, setIsLoading] = useState(false);

  // Default handler used when no onFormSubmit prop is provided (e.g. home page).
  // Fires all 4 department APIs in parallel and takes the first success.
  function defaultFormSubmit(code) {
    if (!code || isLoading) return;
    setIsLoading(true);

    const controllers = ALL_ENDPOINTS.map(() => new AbortController());
    let done = false;
    let rejectedCount = 0;
    const errors = [];

    ALL_ENDPOINTS.forEach((endpoint, i) => {
      requester()
        .get(endpoint, {
          params: { code },
          signal: controllers[i].signal,
        })
        .then((res) => {
          if (done) return;
          done = true;
          controllers.forEach((c, j) => { if (j !== i) c.abort(); });
          setIsLoading(false);
          const albumData = res?.data?.data;
          if (albumData?.serviceType === "album") {
            setAlbum(albumData);
          } else if (albumData?.serviceType === "video") {
            window.open(albumData?.videoUrl, "_top");
          } else if (
            albumData?.serviceType === "photo" ||
            albumData?.tripType === "grand-aquarium" ||
            albumData?.tripType === "media" ||
            albumData?.tripType === "dolphin-house"
          ) {
            window.open(albumData?.albumSharedLink, "_top");
          }
        })
        .catch((err) => {
          if (err?.code === "ERR_CANCELED") return;
          errors.push(err);
          rejectedCount++;
          if (rejectedCount === ALL_ENDPOINTS.length && !done) {
            setIsLoading(false);
            const has402 = errors.some((e) => e?.response?.status === 402);
            toast.info(
              <div style={{ textAlign: "center" }}>
                {has402
                  ? "Hi👋, kindly complete your payment as we agreed with you, thanks"
                  : "Hi👋, your package will be available within 24 hours, thanks"}
              </div>
            );
          }
        });
    });
  }

  const activeSubmitHandler = onFormSubmit || defaultFormSubmit;
  const onFormSubmitRef = useRef(activeSubmitHandler);
  useEffect(() => {
    onFormSubmitRef.current = activeSubmitHandler;
  }, [activeSubmitHandler]);

  function handleContactSubmit(payload, form) {
    if (isLoading) return;

    if (!payload.name || !payload.email || !payload.message) {
      const required = "Please fill in all required fields.";
      if (form) showContactStatus(form, required, "error");
      toast.error(required);
      return;
    }

    setIsLoading(true);
    if (form) setContactFormBusy(form, true);
    requester()
      .post("/contact/send-message", payload)
      .then((res) => {
        if (res?.data?.success === false) {
          throw new Error("not sent");
        }
        const sent = "Thank you! Your message has been sent.";
        if (form) {
          showContactStatus(form, sent, "success");
          clearContactFields(form);
        }
        toast.success(sent);
      })
      .catch(() => {
        const failed = contactFailureMessage();
        if (form) showContactStatus(form, failed, "error");
        toast.error(failed);
      })
      .finally(() => {
        setIsLoading(false);
        if (form) setContactFormBusy(form, false);
      });
  }

  const onContactSubmitRef = useRef(handleContactSubmit);
  useEffect(() => {
    onContactSubmitRef.current = handleContactSubmit;
  });

  useEffect(() => {
    applyWebsiteHead(page);
  }, [page]);

  useEffect(() => {
    const iframe = iframeRef.current;
    if (!iframe) return;

    let observer;
    let debounceTimer;
    let socialSyncTimer;
    let socialSyncAttempts = 0;

    const handleLoad = () => {
      const doc = iframe.contentDocument;
      if (!doc) return;

      interceptGetYourMomentsLinks(doc);
      interceptFramerPageLinks(doc);
      interceptExternalLinks(doc);
      applyWebsiteHead(page, doc);
      runIframeCustomizations(doc, null, initialCode, onFormSubmitRef, onContactSubmitRef, {
        syncSocialLinks: true,
      });

      observer?.disconnect();
      observer = new MutationObserver(() => {
        clearTimeout(debounceTimer);
        debounceTimer = setTimeout(() => {
          if (socialLinksNeedSync(doc)) {
            runIframeCustomizations(doc, observer, initialCode, onFormSubmitRef, onContactSubmitRef, {
              syncSocialLinks: true,
            });
          } else {
            runIframeCustomizations(doc, observer, initialCode, onFormSubmitRef, onContactSubmitRef, {
              syncSocialLinks: false,
            });
          }
        }, 300);
      });
      observer.observe(doc.documentElement, { childList: true, subtree: true });

      // Framer hydration can replace sidebar links after the first paint.
      clearInterval(socialSyncTimer);
      socialSyncAttempts = 0;
      socialSyncTimer = setInterval(() => {
        socialSyncAttempts += 1;
        if (socialSyncAttempts > 20) {
          clearInterval(socialSyncTimer);
          return;
        }
        if (socialLinksNeedSync(doc)) {
          runIframeCustomizations(doc, observer, initialCode, onFormSubmitRef, onContactSubmitRef, {
            syncSocialLinks: true,
          });
        }
      }, 500);
    };

    iframe.addEventListener("load", handleLoad);

    return () => {
      iframe.removeEventListener("load", handleLoad);
      clearTimeout(debounceTimer);
      clearInterval(socialSyncTimer);
      observer?.disconnect();
    };
  }, [page]); // eslint-disable-line

  if (album) {
    return (
      <div
        style={{
          height: "100vh",
          background: "#1a1a1a",
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
        }}
      >
        <div
          style={{
            background: "#2c2c2c",
            borderRadius: "1.5rem",
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            padding: "2rem",
            minWidth: 300,
            gap: "1.5rem",
          }}
        >
          <div
            onClick={() => setAlbum(null)}
            style={{
              display: "flex",
              alignItems: "center",
              gap: "1rem",
              cursor: "pointer",
              alignSelf: "flex-start",
              color: "white",
            }}
          >
            <span style={{ fontSize: "1.2rem" }}>←</span>
            <span>Back</span>
          </div>
          <a
            className="button"
            href={album.albumSharedLink}
            target="_blank"
            rel="noreferrer"
            style={{ width: "100%", textAlign: "center", textDecoration: "none" }}
          >
            Photo
          </a>
          <a
            className="button"
            href={album.videoUrl}
            target="_blank"
            rel="noreferrer"
            style={{ width: "100%", textAlign: "center", textDecoration: "none" }}
          >
            Video
          </a>
        </div>
      </div>
    );
  }

  return (
    <div style={{ position: "relative", height: "100%" }}>
      <iframe
        ref={iframeRef}
        title="Fimtosoft"
        src={`${publicUrl}${src}`}
        className={styles.frame}
      />
      {isLoading && (
        <div
          style={{
            position: "absolute",
            inset: 0,
            background: "rgba(0,0,0,0.5)",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            color: "white",
            fontSize: "1.25rem",
          }}
        >
          {page === "/contact" ? "Sending..." : "Loading..."}
        </div>
      )}
    </div>
  );
}
